import { useContext, type HTMLProps } from 'react';

import { classList } from '@/utils/cn';

import { SmoothdownRenderContext } from '../../smoothdown-render-context';

import { InlineLatex, Latex } from './Latex';
import { Mermaid } from './Mermaid';
import { SyntaxHighlighter } from './SyntaxHighlighter';

export interface StaticCodeProps extends HTMLProps<HTMLElement> {
  children?: React.ReactNode;
}

export function StaticCode(props: StaticCodeProps) {
  const { className, children } = props;
  const { isAnimating } = useContext(SmoothdownRenderContext);
  const charCount = typeof children === 'string' ? children.length : 0;

  if (!className && charCount <= 100) return <InlineCode {...props} />;

  if (className) {
    if (className.startsWith('language-math'))
      return <InlineLatex {...props} />;

    if (className.startsWith('language-latex')) return <Latex {...props} />;

    if (className.startsWith('language-mer')) return <Mermaid {...props} />;
  }

  return (
    <SyntaxHighlighter
      {...props}
      language={className?.substring(9).trim() || 'text'}
      code={(children as string) ?? ''}
      isAnimating={isAnimating}
    />
  );
}

export function InlineCode({ children }: StaticCodeProps) {
  return (
    <code
      className={classList(
        'inline',
        'rounded-sm smoothing-default',
        'font-normal',
        'before:content-none after:content-none',
        'px-1.5 py-0.5',
        'break-words',
        'bg-surface-active'
      )}
    >
      {children}
    </code>
  );
}
